<template>
  <v-row no-gutters>
    <v-col
      cols="3"
      class="pl-6"
    >
      <v-list
        dense
        class="transparent pa-0 mt-12"
      >
        <v-list-item
          v-for="category in categories"
          :key="category.id"
          :style="{ borderLeft: `4px solid ${category.color}`}"
          :class="{ 'v-list-item--active primary--text font-weight-bold': currentCategoryId === category.id }"
          @click="currentCategoryId = category.id"
        >
          <v-list-item-content>
            <v-list-item-title>
              {{ category.name }}
            </v-list-item-title>
            <v-list-item-subtitle
              v-if="category.is_navigare_option"
              class="caption grey--text"
            >
              Custom Option
            </v-list-item-subtitle>
            <v-list-item-subtitle
              v-else-if="category.is_cdr"
              class="caption grey--text"
            >
              CDR
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-chip
              x-small
              label
              :color="category.packsSetCount === 0 ? 'error' : 'grey darken-1'"
              dark
              text-color="white"
              class="font-weight-bold"
            >
              {{ category.packsSetCount }}
            </v-chip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-col>
    <v-col>
      <v-tabs
        v-model="currentCountryId"
        class="ma-0"
        background-color="transparent"
      >
        <v-tab
          v-for="country in countriesWithAssociatedCount"
          :key="country.id"
          :href="'#' + country.id"
          class="text-none"
          @click="$emit('change:country', country.id)"
        >
          <flag
            :iso="country.code"
            :squared="false"
            style="font-size: 10px"
            class="mr-1"
          />
          {{ country.name }}
        </v-tab>
      </v-tabs>

      <v-card elevation="10">
        <v-card-text :class="{'px-5 pt-4 pb-8': !$vuetify.breakpoint.xsOnly }">
          <!--<editor-fold desc="Add eq pack">-->
          <v-row
            no-gutters
            align="center"
          >
            <v-col class="body-2 grey--text text--darken-3">
              Add equipment packs for categories
            </v-col>
            <v-col
              class="shrink"
            >
              <v-btn
                color="secondary"
                text
                @click="openYachtEquipmentPackEditDialog()"
              >
                <v-icon left>
                  add
                </v-icon>
                New Pack
              </v-btn>
            </v-col>
          </v-row>

          <v-autocomplete
            ref="equipmentPacksSelect"
            :items="equipmentPacksByCountry"
            :loading="equipmentPacks.loading"
            name="equipmentPacks"
            label="Select equipment pack"
            item-text="label"
            item-value="id"
            item-avatar="id"
            return-object
            outlined
            hide-details
            two-line
            @change="addEquipmentPack"
          >
            <template v-slot:item="{ item: equipmentPack }">
              <v-list-item-content>
                <v-list-item-title>
                  <v-row no-gutters>
                    <v-col style="min-width: 1%">
                      <div class="text-truncate">
                        {{ equipmentPack.label }}
                      </div>
                    </v-col>
                    <v-col
                      v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                      class="shrink"
                    >
                      {{ equipmentPack.sale_price | currency(equipmentPack.currency) }}
                    </v-col>
                  </v-row>
                </v-list-item-title>
                <v-list-item-subtitle
                  class="caption text-truncate"
                  style="max-width: 540px"
                >
                  <span v-if="equipmentPack.category">
                    <v-avatar
                      size="10px"
                      :color="equipmentPack.category.color"
                      class="mr-1"
                    />
                    {{ equipmentPack.category.name }}
                    &middot;
                  </span>
                  <flag
                    v-for="country in equipmentPack.countries"
                    :key="country.id"
                    :iso="country.code"
                    :squared="false"
                    style="font-size: 12px"
                    class="mr-1"
                  />
                  &middot;
                  {{ equipmentPack.installed_by ? installedByData[equipmentPack.installed_by].label : '' }}
                  &middot;
                  {{ equipmentPack.items.slice(0, 5).map(i => i.label).join(', ') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <!--</editor-fold>-->

          <!--<editor-fold desc="Associated eq packs">-->
          <div class="body-2 grey--text text--darken-3 mt-6 mb-2">
            Associated equipment packs
          </div>
          <div
            v-if="selectedEquipmentPacksByCountryAndCategory.length > 0"
          >
            <template v-for="equipmentPack in selectedEquipmentPacksByCountryAndCategory">
              <v-row
                :key="equipmentPack.id"
                no-gutters
                align="center"
                class="list--bordered caption py-2 clickable"
                @click="openYachtEquipmentViewDialog(equipmentPack.id)"
              >
                <v-col
                  cols="auto"
                  class="px-2"
                >
                  <v-icon
                    color="red"
                    small
                    @click.stop="removeSelectedEquipmentPack(equipmentPack.id)"
                  >
                    close
                  </v-icon>
                </v-col>
                <v-col>
                  <div class="font-weight-bold">
                    {{ equipmentPack.label }}
                  </div>
                  <div class="grey--text caption">
                    <span v-if="equipmentPack.category">
                      {{ equipmentPack.category.name }}
                      &middot;
                    </span>
                    <span v-if="$can('view-sale-price', 'YachtEquipmentPack')">
                      {{ equipmentPack.sale_price | currency(equipmentPack.currency) }}
                      &middot;
                    </span>
                    {{ equipmentPack.installed_by ? installedByData[equipmentPack.installed_by].label : '' }}
                  </div>
                </v-col>
                <v-col
                  v-if="equipmentPack.status !== equipmentPackStatusActive"
                  cols="auto"
                  class="red--text caption pr-4 text-uppercase"
                >
                  {{ equipmentPack.status }}
                </v-col>
                <v-col
                  cols="auto"
                  class="grey--text caption pr-4"
                >
                  add by default
                </v-col>
                <v-col cols="auto">
                  <v-switch
                    v-model="equipmentPack.default"
                    hide-details
                    class="ma-0 pa-0"
                    @click.native.stop
                  />
                </v-col>
                <v-col
                  v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                  class="ml-4 text-right grey--text"
                  style="flex: 0 0 100px"
                >
                  {{ equipmentPack.purchase_price | currency(equipmentPack.currency) }}
                </v-col>
                <v-col
                  v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                  class="ml-4 text-right"
                  style="flex: 0 0 80px"
                  :class="{ 'grey--text': equipmentPack.status !== equipmentPackStatusActive }"
                >
                  {{ equipmentPack.sale_price | currency(equipmentPack.currency) }}
                </v-col>
              </v-row>
            </template>
          </div>
          <v-card
            v-else
            outlined
            class="pa-8 text-center grey--text"
          >
            No equipment packs associated for this category and country. Select above.
          </v-card>
          <!--</editor-fold>-->
          <v-row
            no-gutters
            class="mt-4"
          >
            <v-spacer />
            <v-col cols="auto">
              <v-btn
                color="primary"
                :loading="submitting"
                @click="submit()"
              >
                <v-icon
                  left
                  small
                >
                  fa fa-check
                </v-icon>
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <yacht-equipment-pack-edit-dialog
      :yacht-equipment-pack="yachtEquipmentPackDirty"
      :visible.sync="yachtEquipmentPackEditDialog"
      @yachtEquipmentPack:created="yachtEquipmentPackCreated"
      @yachtEquipmentPack:updated="yachtEquipmentPackUpdated"
    />

    <yacht-equipment-pack-view-dialog
      v-if="yachtEquipmentPackViewDialog.id"
      :visible.sync="yachtEquipmentPackViewDialog.visible"
      :yacht-equipment-pack-id="yachtEquipmentPackViewDialog.id"
      @yacht-equipment-pack:created="yachtEquipmentPackCreated"
      @yacht-equipment-pack:updated="yachtEquipmentPackUpdated"
      @yacht-equipment-pack:deleted="yachtEquipmentPackDeleted"
    />
  </v-row>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapGetters, mapState } from 'vuex'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import YachtModel from '@/models/YachtModel'
import validationScrollMixin from '@/mixins/validationScrollMixin'
import YachtEquipmentPackEditDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackEditDialog'
import YachtEquipmentPackViewDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackViewDialog'

export default {
  name: 'YachtModelDetailsAdditionalEquipment',
  components: {
    YachtEquipmentPackViewDialog,
    YachtEquipmentPackEditDialog
  },
  mixins: [deleteConfirmationMixin, validationScrollMixin],
  props: {
    yachtModel: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      equipmentPacks: {
        data: [],
        loading: false
      },
      currentCountryId: null,
      currentCategoryId: null,
      selectedEquipmentPacks: [],
      submitting: false,
      yachtEquipmentPackDirty: undefined,
      yachtEquipmentPackEditDialog: false,
      yachtEquipmentPackViewDialog: {
        visible: false,
        id: undefined
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedEquipmentPacksByCountryAndCategory() {
      if (!this.currentCategoryId) {
        return [...this.selectedEquipmentPacksByCountry]
      }

      return this.selectedEquipmentPacksByCountry
        .filter(p => p.yacht_equipment_pack_category_id === this.currentCategoryId)
    },
    selectedEquipmentPacksByCountry() {
      return this.selectedEquipmentPacks
        .filter(p => p.countries.some(c => c.id === parseInt(this.currentCountryId)))
    },
    equipmentPacksByCountry() {
      return this.equipmentPacks.data.filter(ep => {
        if (!ep.countries.some(c => c.id === parseInt(this.currentCountryId))) return false

        if (this.currentCategoryId) return ep.yacht_equipment_pack_category_id === this.currentCategoryId

        return true
      })
    },
    categories() {
      return this.equipmentPackCategories.data.filter(c => !c.is_yard_equipment).map(category => {
        return {
          ...category,
          isSet: this.selectedEquipmentPacksByCountry.some(ep => ep.yacht_equipment_pack_category_id === category.id),
          isFiltered: this.currentCategoryId === category.id,
          packsSetCount: this.selectedEquipmentPacksByCountry.filter(ep => ep.yacht_equipment_pack_category_id === category.id).length
        }
      })
    },
    totalDefaultEquipmentPriceForCountry() {
      if (!this.yachtModel) return 0

      let price = this.selectedEquipmentPacksByCountry
        .filter(pack => pack.status === YachtEquipmentPack.STATUS_ACTIVE && pack.default && !pack.category.is_cdr && pack.installed_by !== YachtEquipmentPack.INSTALLED_BY_SHIPYARD)
        .map(pack => this.exchangeCurrency(pack.sale_price || 0, pack.currency, this.yachtModel.currency, this.yachtModel.model_year))
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)

      return Number(price)
    },
    totalDefaultEquipmentPurchasePriceForCountry() {
      if (!this.yachtModel) return 0

      let price = this.selectedEquipmentPacksByCountry
        .filter(pack => pack.status === YachtEquipmentPack.STATUS_ACTIVE && pack.default && !pack.category.is_cdr && pack.installed_by !== YachtEquipmentPack.INSTALLED_BY_SHIPYARD)
        .map(pack => this.exchangeCurrency(pack.purchase_price || 0, pack.currency, this.yachtModel.currency, this.yachtModel.model_year))
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)

      return Number(price)
    },
    totalDefaultEquipmentPriceForCountryByYard() {
      if (!this.yachtModel) return 0

      let price = this.selectedEquipmentPacksByCountry
        .filter(pack => pack.status === YachtEquipmentPack.STATUS_ACTIVE && pack.default && !pack.category.is_cdr && pack.installed_by === YachtEquipmentPack.INSTALLED_BY_SHIPYARD)
        .map(pack => this.exchangeCurrency(pack.sale_price || 0, pack.currency, this.yachtModel.currency, this.yachtModel.model_year))
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)

      return Number(price)
    },
    totalDefaultEquipmentPurchasePriceForCountryByYard() {
      if (!this.yachtModel) return 0

      let price = this.selectedEquipmentPacksByCountry
        .filter(pack => pack.status === YachtEquipmentPack.STATUS_ACTIVE && pack.default && !pack.category.is_cdr && pack.installed_by === YachtEquipmentPack.INSTALLED_BY_SHIPYARD)
        .map(pack => this.exchangeCurrency(pack.purchase_price || 0, pack.currency, this.yachtModel.currency, this.yachtModel.model_year))
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)

      return Number(price)
    },
    cdrPrice() {
      if (!this.yachtModel) return 0

      let price = this.selectedEquipmentPacksByCountry
        .filter(pack => pack.status === YachtEquipmentPack.STATUS_ACTIVE && pack.default && pack.category.is_cdr)
        .map(pack => this.exchangeCurrency(pack.sale_price || 0, pack.currency, this.yachtModel.currency, this.yachtModel.model_year))
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)

      return Number(price)
    },
    cdrPurchasePrice() {
      if (!this.yachtModel) return 0

      let price = this.selectedEquipmentPacksByCountry
        .filter(pack => pack.status === YachtEquipmentPack.STATUS_ACTIVE && pack.default && pack.category.is_cdr)
        .map(pack => this.exchangeCurrency(pack.purchase_price || 0, pack.currency, this.yachtModel.currency, this.yachtModel.model_year))
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)

      return Number(price)
    },
    totalPrice() {
      return Number(this.yachtModel.sale_price) + this.totalDefaultEquipmentPriceForCountry + this.totalDefaultEquipmentPriceForCountryByYard + this.cdrPrice
    },
    totalPurchasePrice() {
      return Number(this.yachtModel.purchase_price) + this.totalDefaultEquipmentPurchasePriceForCountry + this.totalDefaultEquipmentPurchasePriceForCountryByYard + this.cdrPurchasePrice
    },
    currentCountry() {
      return this.yachtModel.countries.find(c => c.id === parseInt(this.currentCountryId))
    },
    requiredEquipmentPackCategories() {
      return this.equipmentPackCategories.data.filter(c => c.required)
    },
    countriesWithAssociatedCount() {
      return this.yachtModel.countries.map(country => {
        return {
          ...country,
          requiredAssociatedCount: this.requiredEquipmentPackCategories
            .filter(c => {
              return this.selectedEquipmentPacks
                .filter(p => p.countries.some(c1 => c1.id === parseInt(country.id)))
                .some(p => p.category.id === c.id)
            }).length
        }
      })
    },
    ...mapGetters('settings', ['equipmentPackCategories']),
    ...mapState('settings', ['currencyExchangeRates'])
  },
  watch: {
    currentCategoryId() {
      this.fetchEquipmentPacks()
    }
  },
  created() {
    this.installedByData = YachtEquipmentPack.installedByData
    this.equipmentPackStatusActive = YachtEquipmentPack.STATUS_ACTIVE

    if (this.yachtModel) {
      this.selectedEquipmentPacks = cloneDeep(this.yachtModel.equipment_packs)
      this.currentCountryId = this.yachtModel.countries[0].id
      this.currentCategoryId = this.categories[0].id
    }

    this.fetchEquipmentPacks()
  },
  methods: {
    async fetchEquipmentPacks() {
      if (!this.currentCategoryId) return

      try {
        this.equipmentPacks.loading = true

        this.equipmentPacks.data = await YachtEquipmentPack.api.fetch({
          status: YachtEquipmentPack.STATUS_ACTIVE,
          order_by: 'yacht_equipment_pack_categories.name',
          yacht_equipment_pack_category_id: this.currentCategoryId
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong.',
          color: 'error'
        })
      } finally {
        this.equipmentPacks.loading = false
      }
    },
    async submit() {
      try {
        this.submitting = true

        let data = {
          equipment_packs: this.selectedEquipmentPacks.map(ep => ({
            id: ep.id,
            default: !!ep.default
          }))
        }

        let yachtModel = await YachtModel.api.associateEquipmentPacks(this.yachtModel.id, data)

        this.$store.dispatch('alert', {
          text: 'Equipment packs for yacht model successfully updated',
          color: 'success'
        })

        this.$emit('update', yachtModel)

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    addEquipmentPack(pack) {
      pack.default = true

      if (!this.selectedEquipmentPacks.some(ep => ep.id === pack.id)) this.selectedEquipmentPacks.push(pack)

      this.$refs.equipmentPacksSelect.reset()
    },
    removeSelectedEquipmentPack(id) {
      this.selectedEquipmentPacks.splice(this.selectedEquipmentPacks.findIndex(ep => ep.id === id), 1)
    },
    findExchangeRate(toCurrency, fromCurrency, year) {
      if (toCurrency === fromCurrency) return 1

      let pair = this.currencyExchangeRates.data.find(exchangeRate => {
        if (exchangeRate.base === fromCurrency && exchangeRate.quote === toCurrency && exchangeRate.year.toString() === year.toString()) return true
      })

      if (!pair) return 0

      return pair.rate
    },
    exchangeCurrency(amount, fromCurrency, toCurrency, year) {
      if (fromCurrency === toCurrency) return amount

      return Number((amount || 0) * this.findExchangeRate(toCurrency, fromCurrency, year))
    },
    async openYachtEquipmentViewDialog(yachtEquipmentPackId = undefined) {
      if (!yachtEquipmentPackId) {
        return
      }

      this.yachtEquipmentPackViewDialog.id = yachtEquipmentPackId
      this.yachtEquipmentPackViewDialog.visible = true
    },
    async openYachtEquipmentPackEditDialog(yachtEquipmentPackId = undefined) {
      if (!yachtEquipmentPackId) {
        this.yachtEquipmentPackDirty = undefined
        this.yachtEquipmentPackEditDialog = true
        return
      }

      try {
        this.yachtEquipmentPackDirty = await YachtEquipmentPack.api.find(yachtEquipmentPackId)

        this.yachtEquipmentPackEditDialog = true
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      }
    },
    yachtEquipmentPackUpdated(yachtEquipmentPack) {
      // update in all eq packs
      this.equipmentPacks.data.splice(this.equipmentPacks.data.findIndex(ep => ep.id === yachtEquipmentPack.id), 1, yachtEquipmentPack)

      // update in selected list
      let selectedEquipmentPackIndex = this.selectedEquipmentPacks.findIndex(ep => ep.id === yachtEquipmentPack.id)
      this.selectedEquipmentPacks.splice(selectedEquipmentPackIndex, 1, {
        ...this.selectedEquipmentPacks[selectedEquipmentPackIndex],
        ...yachtEquipmentPack
      })
    },
    yachtEquipmentPackCreated(yachtEquipmentPack) {
      this.equipmentPacks.data.unshift(yachtEquipmentPack)
    },
    yachtEquipmentPackDeleted(yachtEquipmentPack) {
      // update in all eq packs
      this.equipmentPacks.data.splice(this.equipmentPacks.data.findIndex(ep => ep.id === yachtEquipmentPack.id), 1)

      // update in selected list
      let selectedEquipmentPackIndex = this.selectedEquipmentPacks.findIndex(ep => ep.id === yachtEquipmentPack.id)
      this.selectedEquipmentPacks.splice(selectedEquipmentPackIndex, 1)
    }
  }
}
</script>
